'use client';
import { ErrorState } from '@components/Form';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { ModalID, useModal } from '@repo/ui/src/modules/UI';
import React from 'react';
import Loading from '@components/Loading';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import useBasket from '@hooks/basket/useBasket';
import CartBody from './_CartBody';
import FlameIcon from '@repo/ui/src/components/Icons/FlameIcon';
import OrderSummary from './_OrderSummary';
import CartFooter from './_CartFooter';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import { Anchor } from '@repo/ui/src/components/Anchor';

interface CartPageViewProps {
  basket?: any;
  locale?: string;
  isLoading?: boolean;
  outOfStockItems?: string[];
}

const CartPageView = ({ outOfStockItems }: CartPageViewProps) => {
  const { data: basket, isLoading } = useBasket();
  const { locale, push } = useCurrentUrl();
  const [checkoutError, setCheckoutError] = React.useState(false);
  const { push: pushCheckoutLoader, close: closeCheckoutLoader } = useModal(ModalID.checkoutLoader);

  const { t } = useTranslation();
  const { totalItems = 0 } = basket || {};
  const isEmpty = !totalItems;

  const navigateToCheckout = async () => {
    pushCheckoutLoader();
    close();
    const result = await fetch(`/${locale}/checkout`);
    if (result.redirected) {
      closeCheckoutLoader();
      return setCheckoutError(true);
    }
    push(`/${locale}/checkout`);
  };

  const skus = basket?.lineItems?.map((lineItem) => lineItem.sku);
  const disabled = outOfStockItems?.some((element: string) => skus?.includes(element));

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="tw-grid tw-min-h-screen md:tw-grid-cols-[2fr_1fr]">
      <div className="tw-flex tw-px-6 tw-mt-5 md:tw-mt-20 tw-justify-center">
        <div className="tw-max-w-[52rem] tw-w-full">
          {checkoutError && (
            <ErrorState
              text={t('cart.errorStateText', undefined, {
                default: 'Something went wrong. Please try again.',
              })}
            />
          )}
          <div className="md:tw-ml-6 tw-flex-1">
            <div className="tw-flex">
              <FlameIcon width={44} outline className="md:tw-mt-4 tw-mr-3 md:tw-mr-6" />
              <div>
                <h1 className="tra-text-web-heading-3 tw-mb-3">
                  {t('cart.title', undefined, { default: 'Your Cart' })}
                </h1>
                <p className="tra-text-web-body-m tw-font-bold tw-mb-6">
                  {t('cart.cartSubtitle', undefined, {
                    default: "You're almost ready to get cooking.",
                  })}
                </p>
              </div>
            </div>
            <div className="md:tw-ml-16">
              <CartBody isEmpty={isEmpty} outOfStockItems={outOfStockItems} showSummary={false} />
            </div>
          </div>
        </div>
      </div>

      <div className="tw-bg-gray-100 tw-flex tw-flex-col tw-items-start tw-justify-start">
        <div className="tw-w-full tw-mt-20 tw-mb-6 tw-px-8 tw-sticky tw-top-[calc(var(--navTop)_+_1rem)]">
          <h1 className="tra-text-web-heading-4 tw-mb-6 tw-text-2xl tw-font-bold">
            {t('orderSummary.orderSummaryTitle', undefined, { default: 'ORDER SUMMARY' })}
          </h1>
          <hr className="tw-w-full tw-border-surface-200" />
          <div className="tw-my-5">
            <OrderSummary isCartPage />
          </div>
          <hr className="tw-w-full tw-border-surface-200" />
          <div className="tw-mt-5 tw-w-full">
            <CartFooter
              isLoading={isLoading}
              isEmpty={isEmpty}
              disabled={disabled}
              navigateToCheckout={navigateToCheckout}
            />
          </div>
          <Button
            variant={ButtonVariant.knife}
            full
            size="small"
            className="tw-justify-center tw-mb-8"
            link="/"
            as={Anchor}
          >
            {t('cart.continueShopping', undefined, { default: 'Continue Shopping' })}
          </Button>
          <hr className="tw-w-full tw-border-surface-200" />
        </div>
      </div>
    </div>
  );
};

export default CartPageView;
